import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  GENDERS,
  LANGUAGE_DATA,
  LANGUAGE_VOICES,
  getVoiceFromValue,
} from "../../../../constants/scriptConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import VoiceOptionsList from "./VoiceOptionList";
import { useDispatch } from "react-redux";
import {
  setElevenLabsVoice,
  setOpenAiVoiceId,
  updateAccentVoice,
  updateGeneratedVoice,
  updateSlideLanguage,
  updateSlideVoice,
  updateSlideVoiceFile,
} from "../../../../store/slides/slidesSlice";
import { useSelector } from "react-redux";
import Loader from "../../Loader";
import { showToast } from "../../../../utils/general";
import Sidebar from "../Sidebar";
import {
  getAllElevenLabVoicesAccentedVoice,
  getElevenLabsVoices,
} from "../../../../thunks/editor/scriptThunks";
import { selectSubTab, setAudioSubTab } from "../../../../store/common/uiSlice";
import ElevanlabsAccents from "./ElevanlabsAccents";
import OpenAiVoice from "./OpenAiVoice";

export const audioVoiceTypes = {
  NEW: "ElevenLabs Voices",
  OLD: "Microsoft Azure Voices",
  ACCENTS: "Accents Voices",
  OPENAIVOICE: "OpenAI Voices",
};
export const subSidebar = [
  {
    name: audioVoiceTypes.NEW,
    value: audioVoiceTypes.NEW,
    getVoices: getElevenLabsVoices,
  },
  {
    name: audioVoiceTypes.OLD,
    value: audioVoiceTypes.OLD,
  },
  {
    name: audioVoiceTypes.ACCENTS,
    value: audioVoiceTypes.ACCENTS,
    getVoices: getAllElevenLabVoicesAccentedVoice,
    // disabled: useAi,
  },
  {
    name: audioVoiceTypes.OPENAIVOICE,
    value: audioVoiceTypes.OPENAIVOICE,
    // disabled: useAi,
  },
];

const VoiceOptions = ({
  onClose,
  onVoiceChange,
  useAi,
  voiceOptionClassName,
}) => {
  const dispatch = useDispatch();

  const { currentSlideIndex, slides } = useSelector((state) => state.slides);
  const { selectedSlideData } = useSelector((state) => state.script);
  const { isFaceless } = useSelector((state) => state.project);

  const { elevenLabsVoices } = useSelector((state) => state.script);
  const { audioSubTab } = useSelector((state) => state.ui);
  const appState = useSelector((state) => state.appState);

  const currentSlide = useMemo(() => {
    return useAi
      ? slides[selectedSlideData?.currentSelectSlide || 0]
      : slides[currentSlideIndex];
  }, [currentSlideIndex, slides, selectedSlideData]);

  const elevenLabs = useMemo(
    () => currentSlide?.elevenlabs_voice,
    [currentSlide]
  );
  const isElevenLabsTabSelected = useMemo(
    () => subSidebar[audioSubTab]?.name === audioVoiceTypes.NEW,
    [audioSubTab]
  );

  const isAccentVoice = useMemo(
    () => subSidebar[audioSubTab]?.name === audioVoiceTypes.ACCENTS,
    [audioSubTab]
  );

  const isOpenAiVoice = useMemo(
    () => subSidebar[audioSubTab]?.name === audioVoiceTypes.OPENAIVOICE,
    [audioSubTab]
  );

  const { voice, avatarGender } = useMemo(() => {
    let avatar = (currentSlide.objects || []).find(
      (obj) => obj.type === "avatar"
    );
    if (useAi && !avatar) {
      avatar = { gender: getVoiceFromValue(selectedSlideData.voice)?.gender };
    }
    return {
      voice:
        useAi && isAccentVoice
          ? selectedSlideData?.voice
          : useAi
          ? (
              selectedSlideData?.elevenLabVoice?.languageId ||
              selectedSlideData?.voice
            )?.slice(0, 5)
          : currentSlide?.voice?.slice(0, 5) || "en-US",
      avatarGender: (avatar?.gender || "").toLowerCase() || "male",
    };
  }, [currentSlide, selectedSlideData]);

  const [gender, setGender] = useState(avatarGender);

  const [language, setLanguage] = useState(
    elevenLabs ? elevenLabs.languageId.slice(0, 5) : voice
  );

  const onVoiceSelected = (val) => {
    if (onVoiceChange) {
      onVoiceChange(val, language, gender);
    }
    // sync with chatgpt logic.  GenerateScript onVoiceChange function
    if (isElevenLabsTabSelected) {
      if (!useAi) {
        const find = elevenLabsVoices.find((v) => v.id === val);

        const selectedLanguage =
          LANGUAGE_VOICES.find(
            (v) =>
              v.id.includes(language) &&
              v.gender.toLowerCase() === (gender || "male").toLowerCase()
          ) || LANGUAGE_VOICES[0];

        if (find) {
          dispatch(
            setElevenLabsVoice({
              ...find,
              language: selectedLanguage.language,
              languageId: selectedLanguage.id,
            })
          );
          dispatch(updateAccentVoice(null));
          dispatch(setOpenAiVoiceId(null));
        } else {
          showToast("Invalid Voice", "", "danger");
          return;
        }
      }
    } else if (isOpenAiVoice) {
      dispatch(setOpenAiVoiceId(val));
      dispatch(setElevenLabsVoice(null));
      dispatch(updateAccentVoice(null));
    } else if (isAccentVoice) {
      dispatch(updateAccentVoice(val));
      dispatch(setOpenAiVoiceId(null));
      dispatch(setElevenLabsVoice(null));
    } else {
      dispatch(setElevenLabsVoice(null));
      dispatch(setOpenAiVoiceId(null));
      dispatch(updateAccentVoice(null));
    }

    if (!useAi) {
      dispatch(updateSlideVoice(isAccentVoice ? val?.accent : val));
      dispatch(updateGeneratedVoice(""));
      dispatch(updateSlideVoiceFile(""));
    }
    onClose();
  };
  useEffect(() => {
    dispatch(updateSlideLanguage(language.replace("-", "")));
  }, [dispatch, language]);

  const voiceOptions = useMemo(() => {
    if (isElevenLabsTabSelected) {
      return elevenLabsVoices.filter(
        (voice) => gender === "both" || voice?.gender?.toLowerCase() === gender
      );
    }
    return LANGUAGE_VOICES.filter((voice) => {
      return (
        voice.id.includes(language) &&
        (gender === "both" || voice.gender.toLowerCase() === gender)
      );
    });
  }, [gender, language, elevenLabsVoices, isElevenLabsTabSelected]);

  const onTabSelect = useCallback(
    (tabIndex) => {
      dispatch(setAudioSubTab(tabIndex));
      if (subSidebar[tabIndex].getVoices) {
        dispatch(subSidebar[tabIndex].getVoices());
      }
    },
    [dispatch]
  );

  return (
    <div className="flex w-full h-full">
      <Sidebar
        titleIcon={"/tool/dashboard/static/images/icons/tools/script.svg"}
        title={"Script"}
        tabs={subSidebar}
        onTabSelect={onTabSelect}
        activeIndex={audioSubTab}
        className={"min-w-[100px]"}
      />
      <div className="px-2 pr-0 py-4 w-4/5 bg-gray-100 overflow-auto">
        {appState === "loading" && (
          <div className="flex flex-grow h-4/5 justify-center text-sm text-gray-500 items-center text-center w-full">
            <Loader />
          </div>
        )}
        {audioSubTab !== 2 && audioSubTab !== 3 && (
          <div className="pb-1 h-full">
            <div
              htmlFor="scriptInput"
              className="w-full mb-4 text-sm text-gray-700 flex items-center"
            >
              <FontAwesomeIcon
                icon={faArrowLeft}
                onClick={onClose}
                className="mr-2 p-1 h-4 border-gray-500 border rounded-lg cursor-pointer"
              />
              <span className="text-gray-500 cursor-pointer" onClick={onClose}>
                {" "}
                BACK{" "}
              </span>
              <span className="font-bold ml-4">
                Choose Language and voice for slide
              </span>
            </div>

            <div className="flex space-x-2 items-end">
              <div className="flex flex-col">
                {isElevenLabsTabSelected && (
                  <label className="text-sm mb-2">Select language</label>
                )}
                <select
                  defaultValue={language}
                  onChange={(e) => {
                    setLanguage(e.target.value);
                  }}
                  className="w-1/2 p-2 border border-gray-300 text-xs rounded-lg"
                >
                  {LANGUAGE_DATA.map((language, index) => (
                    <option key={index} value={language.value}>
                      {language.language}
                    </option>
                  ))}
                </select>
              </div>

              <select
                defaultValue={gender}
                disabled={!isFaceless}
                className="w-1/4 p-2 border border-gray-300 text-xs rounded-lg"
                onChange={(e) => {
                  setGender(e.target.value);
                }}
              >
                {GENDERS.map((gender, index) => (
                  <option key={index} value={gender.value}>
                    {gender.name}
                  </option>
                ))}
              </select>
            </div>

            <VoiceOptionsList
              voiceOptions={voiceOptions}
              country={isElevenLabsTabSelected ? "" : language.slice(2)}
              onVoiceSelected={onVoiceSelected}
              voiceOptionClassName={voiceOptionClassName}
            />
          </div>
        )}

        {audioSubTab === 2 && (
          <ElevanlabsAccents
            onClose={onClose}
            onVoiceSelected={onVoiceSelected}
          />
        )}
        {audioSubTab === 3 && (
          <OpenAiVoice onClose={onClose} onVoiceSelected={onVoiceSelected} />
        )}
      </div>
    </div>
  );
};

export default VoiceOptions;
